import { createStore, type Store } from './store';

export interface PlayerState {
  currentUs: number;
  durationUs: number;
  playing: boolean;
  buffering: boolean;
}

/**
 * Wraps a media element so the editor can scrub, loop inside the trim range and
 * follow the playhead without React re-rendering on every frame.
 */
export class ClipPlayer {
  readonly id = `player-${Math.random().toString(36).slice(2, 9)}`;
  readonly element: HTMLVideoElement;
  readonly store: Store<PlayerState>;

  private inUs = 0;
  private outUs = Number.POSITIVE_INFINITY;
  private looping = true;
  private frameHandle: number | null = null;
  private interval: ReturnType<typeof setInterval> | null = null;
  private disposed = false;

  constructor(url: string) {
    this.store = createStore<PlayerState>({
      currentUs: 0,
      durationUs: 0,
      playing: false,
      buffering: false,
    });

    this.element = document.createElement('video');
    this.element.className = 'monitor-surface';
    this.element.src = url;
    this.element.playsInline = true;
    this.element.preload = 'auto';
    this.element.controls = false;

    this.element.addEventListener('loadedmetadata', () => this.sync());
    this.element.addEventListener('durationchange', () => this.sync());
    this.element.addEventListener('timeupdate', () => this.sync());
    this.element.addEventListener('play', () => this.sync());
    this.element.addEventListener('pause', () => this.sync());
    this.element.addEventListener('seeking', () => this.sync());
    this.element.addEventListener('waiting', () =>
      this.store.setState({ buffering: true }),
    );
    this.element.addEventListener('playing', () =>
      this.store.setState({ buffering: false }),
    );
    this.element.addEventListener('ended', () => {
      if (this.looping) this.seekToStart();
      else this.store.setState({ playing: false });
    });
  }

  private sync(): void {
    const currentUs = Math.round(this.element.currentTime * 1_000_000);
    const durationUs = Number.isFinite(this.element.duration)
      ? Math.round(this.element.duration * 1_000_000)
      : 0;
    this.store.setState({
      currentUs,
      durationUs,
      playing: !this.element.paused && !this.element.ended,
    });
    if (this.store.getSnapshot().playing && currentUs >= this.outUs) {
      if (this.looping) this.seekUs(this.inUs);
      else this.pause();
    }
  }

  setRange(inUs: number, outUs: number): void {
    this.inUs = inUs;
    this.outUs = outUs;
    const { currentUs } = this.store.getSnapshot();
    if (currentUs < inUs || currentUs > outUs) this.seekUs(inUs);
  }

  setLooping(looping: boolean): void {
    this.looping = looping;
  }

  play(): void {
    const { currentUs } = this.store.getSnapshot();
    if (currentUs < this.inUs || currentUs >= this.outUs) this.seekUs(this.inUs);
    void this.element.play();
  }

  pause(): void {
    this.element.pause();
  }

  toggle(): void {
    if (this.store.getSnapshot().playing) this.pause();
    else this.play();
  }

  seekUs(us: number): void {
    const { durationUs } = this.store.getSnapshot();
    const target = Math.max(0, Math.min(us, durationUs || us));
    this.element.currentTime = target / 1_000_000;
    this.sync();
  }

  stepFrames(frames: number, frameUs: number): void {
    this.pause();
    this.seekUs(this.store.getSnapshot().currentUs + frames * frameUs);
  }

  private seekToStart(): void {
    this.seekUs(this.inUs);
    if (this.looping) void this.element.play();
  }

  /** High-frequency playhead updates while playing. */
  startFollow(): () => void {
    const tick = () => {
      this.sync();
      if (this.disposed) return;
      if (typeof this.element.requestVideoFrameCallback === 'function') {
        this.frameHandle = this.element.requestVideoFrameCallback(tick);
      }
    };
    if (typeof this.element.requestVideoFrameCallback === 'function') {
      this.frameHandle = this.element.requestVideoFrameCallback(tick);
    } else {
      this.interval = setInterval(() => this.sync(), 60);
    }
    return () => {
      if (this.frameHandle !== null && typeof this.element.cancelVideoFrameCallback === 'function') {
        this.element.cancelVideoFrameCallback(this.frameHandle);
      }
      if (this.interval) clearInterval(this.interval);
      this.frameHandle = null;
      this.interval = null;
    };
  }

  dispose(): void {
    this.disposed = true;
    this.pause();
    this.element.removeAttribute('src');
    this.element.load();
  }
}
